import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Plus, Eye, EyeOff, X, AlertTriangle, Package, Save, ShieldCheck, ChevronDown, ChevronUp } from 'lucide-react'
import { PageHeader } from '@/components/shared/PageHeader'
import { SearchInput } from '@/components/shared/SearchInput'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { UsuariosTable } from '@/features/usuarios/UsuariosTable'
import { RoleAccessCard, ActionBadge } from '@/features/roles/RoleAccessCard'
import { useRoleEntitlements } from '@/features/roles/useRoleEntitlements'
import { useAssetCatalog } from '@/features/assets/useAssetCatalog'
import { useAccess } from '@/hooks/useAccess'
import { useAppStore } from '@/store/appStore'
import {
  listIdentities,
  listOrgPermissions,
  listPermissions,
  updateIdentity,
  addRoleToPermission,
  revokeAssetUserRole,
  listPrivileges,
  createPrivilege,
  deletePrivilege,
  guardAction,
  getRolesCatalog,
  createOrgUser,
} from '@/features/ksec/api'
import type { KsecUser, CreateUserForm } from '@/features/usuarios/types'
import { ActionEnum } from '@/features/ksec/types'
import type { KsecDigitalIdentity, KsecPermission, KsecPrivilege, KsecRoleCatalog, ActionType } from '@/features/ksec/types'
import { cn } from '@/lib/utils'

// ─── Password rules ───────────────────────────────────────────────────────────

const PASSWORD_RULES = [
  { label: 'Mínimo 8 caracteres', test: (p: string) => p.length >= 8 },
  { label: 'Al menos 1 mayúscula', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'Al menos 1 minúscula', test: (p: string) => /[a-z]/.test(p) },
  { label: 'Al menos 1 dígito o símbolo', test: (p: string) => /[\d^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+\u002D]/.test(p) },
]

function validatePassword(p: string) {
  return PASSWORD_RULES.every((r) => r.test(p))
}

function isValidEmail(e: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)
}


// ─── Create User Modal ────────────────────────────────────────────────────────

import type { AssetCatalog } from '@/features/assets/useAssetCatalog'

interface CreateUserModalProps {
  open: boolean
  onClose: () => void
  onCreated: () => void
  catalog: AssetCatalog
}

function CreateUserModal({ open, onClose, onCreated, catalog }: CreateUserModalProps) {
  const { identity, org, roles } = useAppStore()
  const [form, setForm] = useState<Omit<CreateUserForm, 'involvementRole'>>({
    email: '',
    firstName: '',
    lastName: '',
    nickname: '',
    initialPassword: '',
    confirmPassword: '',
  })
  const [showPw, setShowPw] = useState(false)
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({})
  const [apiError, setApiError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  // Roles disponibles en el catálogo KSEC (reemplaza assets+roles eliminados en v3.0)
  const [availableRoles, setAvailableRoles] = useState<KsecRoleCatalog[]>([])
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null)
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [expandedRoleId, setExpandedRoleId] = useState<string | null>(null)

  // Caché de entitlements por rol (hook reutilizable)
  const roleEntitlements = useRoleEntitlements()

  // Auto-genera el nickname a partir de Nombre + Apellido ("juan.perez")
  function buildNickname(first: string, last: string): string {
    const f = first.trim().toLowerCase().replace(/\s+/g, '')
    const l = last.trim().toLowerCase().replace(/\s+/g, '')
    if (f && l) return `${f}.${l}`
    return f || l
  }

  function handleFirstNameChange(value: string) {
    setForm((prev) => ({
      ...prev,
      firstName: value,
      nickname: buildNickname(value, prev.lastName),
    }))
  }

  function handleLastNameChange(value: string) {
    setForm((prev) => ({
      ...prev,
      lastName: value,
      nickname: buildNickname(prev.firstName, value),
    }))
  }

  const pwRules = PASSWORD_RULES.map((r) => ({ ...r, passed: r.test(form.initialPassword) }))
  const pwsMatch = form.initialPassword === form.confirmPassword && form.confirmPassword.length > 0

  // Cargar catálogo de roles de la organización actual (filtrado por businessTenantId)
  useEffect(() => {
    if (!open) return
    setLoadingProducts(true)
    const tenantId = org.current?.businessTenantId ?? undefined
    getRolesCatalog(identity.ksecId, tenantId)
      .then((res) => {
        const raw = res.data as { items?: KsecRoleCatalog[] } | KsecRoleCatalog[]
        const roles: KsecRoleCatalog[] = Array.isArray(raw) ? raw : (raw?.items ?? [])
        setAvailableRoles(roles)
      })
      .catch(() => setAvailableRoles([]))
      .finally(() => setLoadingProducts(false))
  }, [open, identity.ksecId, org.current?.businessTenantId])

  const selectRole = (roleId: string) => {
    setSelectedRoleId(roleId)
  }

  function toggleExpand(roleId: string) {
    if (expandedRoleId === roleId) {
      setExpandedRoleId(null)
      return
    }
    setExpandedRoleId(roleId)
    roleEntitlements.loadFor(roleId)
  }

  // Reset al cerrar
  useEffect(() => {
    if (!open) {
      setForm({ email: '', firstName: '', lastName: '', nickname: '', initialPassword: '', confirmPassword: '' })
      setErrors({})
      setApiError(null)
      setAvailableRoles([])
      setSelectedRoleId(null)
      setExpandedRoleId(null)
    }
  }, [open])

  function validate(): boolean {
    const e: Record<string, string> = {}
    if (!isValidEmail(form.email)) e.email = 'Email inválido'
    if (!form.firstName.trim() || form.firstName.trim().length < 2) e.firstName = 'Mínimo 2 caracteres'
    if (!form.lastName.trim() || form.lastName.trim().length < 2) e.lastName = 'Mínimo 2 caracteres'
    if (!validatePassword(form.initialPassword)) e.initialPassword = 'La contraseña no cumple los requisitos'
    if (!pwsMatch) e.confirmPassword = 'Las contraseñas no coinciden'
    if (!selectedRoleId) e.products = 'Selecciona un rol'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return
    setApiError(null)

    // Guard server-side (v3.0): skipped for users whose authorization comes from
    // their admin role rather than an explicit pe.usuarios entitlement.
    // Admin roles are resolved dynamically from the tenant catalog (availableRoles),
    // cross-referenced against the caller's assigned roles (roles.map).
    const catalogAdminRoles = new Set(
      availableRoles
        .map((r) => r.involvementRole)
        .filter((r) => r.toUpperCase().includes('ADMIN')),
    )
    const callerHasAdminRole =
      identity.isKashioSuper ||
      Object.keys(roles.map).some((r) => catalogAdminRoles.has(r))
    if (!callerHasAdminRole) {
      const allowed = await guardAction(identity.ksecId, 'pe.usuarios', 'RW')
      if (!allowed) {
        setApiError('No tienes permisos para crear usuarios.')
        return
      }
    }

    const party_id = org.current?.businessTenantId
    if (!party_id) {
      setApiError('No se pudo determinar el tenant de la organización. Recarga la página.')
      return
    }

    // Determinar el rol seleccionado (involvementRole)
    const primaryRole = availableRoles.find((r) => r.id === selectedRoleId)
    if (!primaryRole) {
      setApiError('No se pudo determinar el rol seleccionado.')
      return
    }

    setLoading(true)
    try {
      // ── Flujo bootstrap: crea primero en KBRM, luego en KSEC ─────────────
      // El backend orquesta:
      //   1. POST KBRM /individuals (bloqueante)
      //   2. Si KBRM 200 → crea DigitalIdentity + Credential + Permission + AssetUserRole
      //   3. Si rol ADMIN → crea Entitlements automáticamente (best-effort)
      //
      // callerPartyRolePublicId: prl_xxx del admin que crea el usuario.
      // El backend lo graba como granter_public_id con granter_type = 'PartyRole'.
      // Si el admin no tiene partyRolePublicId (ej: Kashio Super), no se envía y el backend
      // usa el UUID de Kashio Platform como granter (Party).
      const callerPrl = identity.partyRolePublicId || undefined

      await createOrgUser({
        party_id,
        callerPartyRolePublicId: callerPrl,
        email:     form.email,
        password:  form.initialPassword,
        firstName: form.firstName.trim(),
        lastName:  form.lastName.trim(),
        nickname:  form.nickname || buildNickname(form.firstName, form.lastName),
        rol:       primaryRole.involvementRole,
      })

      onCreated()
      onClose()
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number; data?: { error?: { message?: string } } } })
        ?.response?.status
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data
        ?.error?.message
      if (status === 409) setApiError('El email ya existe en el sistema.')
      else if (status === 502)
        setApiError('No se pudo crear el usuario en KBRM, verifica la contraseña y los datos.')
      else if (status === 400) setApiError(msg ?? 'Datos inválidos, revisa el formulario.')
      else setApiError(msg ?? 'Error al crear el usuario. Intenta de nuevo.')
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-2xl border border-border-secondary bg-bg-primary shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-border-secondary px-6 py-4">
          <h2 className="font-display font-bold text-text-primary">Nuevo usuario</h2>
          <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="space-y-4">
            {apiError && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                {apiError}
              </div>
            )}

            <div className="space-y-1">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="usuario@empresa.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              {errors.email && <p className="text-xs text-text-error">{errors.email}</p>}
            </div>

            {/* Nombre + Apellido en 2 columnas */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="firstName">Nombre</Label>
                <Input
                  id="firstName"
                  placeholder="Juan"
                  value={form.firstName}
                  onChange={(e) => handleFirstNameChange(e.target.value)}
                />
                {errors.firstName && <p className="text-xs text-text-error">{errors.firstName}</p>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="lastName">Apellido</Label>
                <Input
                  id="lastName"
                  placeholder="Pérez"
                  value={form.lastName}
                  onChange={(e) => handleLastNameChange(e.target.value)}
                />
                {errors.lastName && <p className="text-xs text-text-error">{errors.lastName}</p>}
              </div>
            </div>

            {/* Nickname auto-generado */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label htmlFor="nickname">Nickname</Label>
                <span className="text-xs text-text-tertiary">Auto-generado</span>
              </div>
              <Input
                id="nickname"
                placeholder="nombre.apellido"
                value={form.nickname}
                onChange={(e) => setForm({ ...form, nickname: e.target.value })}
                className="bg-bg-secondary text-text-secondary"
                readOnly
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="pw">Contraseña inicial</Label>
              <div className="relative">
                <Input
                  id="pw"
                  type={showPw ? 'text' : 'password'}
                  placeholder="Mínimo 8 caracteres"
                  value={form.initialPassword}
                  onChange={(e) => setForm({ ...form, initialPassword: e.target.value })}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary"
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {form.initialPassword.length > 0 && (
                <ul className="mt-1.5 space-y-1">
                  {pwRules.map((r) => (
                    <li key={r.label} className="flex items-center gap-1.5 text-xs">
                      <span
                        className={cn(
                          'h-1.5 w-1.5 shrink-0 rounded-full',
                          r.passed ? 'bg-green-500' : 'bg-red-400',
                        )}
                      />
                      <span className={r.passed ? 'text-green-700' : 'text-text-error'}>{r.label}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="confirm">Confirmar contraseña</Label>
              <Input
                id="confirm"
                type="password"
                placeholder="Repite la contraseña"
                value={form.confirmPassword}
                onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
              />
              {form.confirmPassword.length > 0 && !pwsMatch && (
                <p className="text-xs text-text-error">Las contraseñas no coinciden.</p>
              )}
            </div>

            {/* Roles de acceso */}
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Package size={14} className="text-brand-600" />
                <Label>Roles de acceso</Label>
              </div>

              {loadingProducts ? (
                <div className="flex items-center gap-2 rounded-lg border border-border-secondary bg-bg-secondary px-4 py-3 text-sm text-text-tertiary">
                  <svg className="h-4 w-4 animate-spin text-brand-600" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                  </svg>
                  Cargando roles disponibles…
                </div>
              ) : availableRoles.length === 0 ? (
                <p className="rounded-lg border border-border-secondary bg-bg-secondary px-4 py-3 text-sm text-text-tertiary">
                  No se encontraron roles disponibles para esta organización.
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {availableRoles.map((r) => {
                    const isSelected = selectedRoleId === r.id
                    const isExpanded = expandedRoleId === r.id

                    return (
                      <div
                        key={r.id}
                        className={cn(
                          'rounded-lg border transition-colors cursor-pointer',
                          isSelected
                            ? 'border-brand-300 bg-brand-50'
                            : 'border-border-secondary bg-bg-secondary',
                        )}
                        onClick={() => selectRole(r.id)}
                      >
                        {/* Fila principal */}
                        <div className="flex items-center gap-3 px-4 py-3">
                          <input
                            type="radio"
                            name="roleSelection"
                            className="h-4 w-4 accent-brand-600 shrink-0"
                            checked={isSelected}
                            onChange={() => selectRole(r.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-xs font-bold text-brand-600">
                            {(r.name ?? r.involvementRole).charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="truncate text-sm font-medium text-text-primary">
                              {r.name ?? r.involvementRole.replace(/_/g, ' ')}
                            </p>
                            {r.name && (
                              <p className="truncate text-xs text-text-tertiary">{r.involvementRole}</p>
                            )}
                          </div>
                          {/* Toggle ver accesos */}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleExpand(r.id) }}
                            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-text-tertiary hover:text-brand-600"
                            title={isExpanded ? 'Ocultar accesos' : 'Ver accesos'}
                          >
                            {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                            <span className="hidden sm:inline">{isExpanded ? 'Ocultar' : 'Ver accesos'}</span>
                          </button>
                        </div>

                        {/* Entitlements expandidos — agrupados por producto */}
                        {isExpanded && (
                          <div className="border-t border-border-secondary px-4 pb-3 pt-3">
                            <p className="mb-2 text-xs font-semibold text-text-tertiary uppercase tracking-wide">
                              Accesos que otorga este rol
                            </p>
                            <RoleAccessCard
                              entitlements={roleEntitlements.get(r.id) ?? []}
                              loading={roleEntitlements.isLoading(r.id)}
                              catalog={catalog}
                              compact
                            />
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
              {errors.products && <p className="text-xs text-text-error">{errors.products}</p>}
            </div>
          </div>
          </div>

          {/* Actions — fixed footer, always visible */}
          <div className="shrink-0 border-t border-border-secondary px-6 py-4 flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Creando...' : 'Crear usuario'}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  )
}

// ─── Edit User Modal ──────────────────────────────────────────────────────────

interface EditUserModalProps {
  user: KsecDigitalIdentity | null
  open: boolean
  onClose: () => void
  onUpdated: () => void
}

function EditUserModal({ user, open, onClose, onUpdated }: EditUserModalProps) {
  const [nickname, setNickname] = useState('')
  const [status, setStatus] = useState<'ACTIVE' | 'INACTIVE' | 'PENDING' | 'BLOCKED'>('ACTIVE')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (user) {
      setNickname(user.nickname ?? '')
      setStatus((user.status as 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'BLOCKED') ?? 'ACTIVE')
      setError(null)
    }
  }, [user])

  if (!open || !user) return null

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!user) return
    if (!nickname.trim() || nickname.trim().length < 3) {
      setError('El nickname debe tener al menos 3 caracteres y sin espacios.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      await updateIdentity(user.id, {
        nickname: nickname.trim(),
        status,
      })
      onUpdated()
      onClose()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message
      setError(msg ?? 'Error al actualizar el usuario.')
    } finally {
      setLoading(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-primary shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border-secondary px-6 py-4">
          <h2 className="font-display font-bold text-text-primary">Editar usuario</h2>
          <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5">
          <div className="space-y-4">
            {error && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                {error}
              </div>
            )}

            {/* ID de referencia (v3.0: email eliminado de DigitalIdentity) */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-text-secondary">ID de identidad</label>
              <p className="rounded-lg border border-border-secondary bg-bg-secondary px-3 py-2 font-mono text-xs text-text-tertiary">
                {user.id}
              </p>
            </div>

            {/* Nickname */}
            <div className="space-y-1">
              <label htmlFor="edit-nickname" className="text-sm font-medium text-text-secondary">
                Nickname
              </label>
              <input
                id="edit-nickname"
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                className="w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary placeholder:text-text-quaternary focus:border-border-brand focus:outline-none"
                placeholder="nombre.apellido"
              />
            </div>

            {/* Estado */}
            <div className="space-y-1">
              <label htmlFor="edit-status" className="text-sm font-medium text-text-secondary">
                Estado
              </label>
              <select
                id="edit-status"
                value={status}
                onChange={(e) => setStatus(e.target.value as 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'BLOCKED')}
                className="w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary focus:border-border-brand focus:outline-none"
              >
                <option value="ACTIVE">Activo</option>
                <option value="INACTIVE">Inactivo</option>
                <option value="PENDING">Pendiente</option>
                <option value="BLOCKED">Bloqueado</option>
              </select>
            </div>
          </div>

          <div className="mt-5 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-border-primary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-bg-secondary"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 rounded-lg bg-bg-brand-solid px-4 py-2 text-sm font-medium text-fg-white hover:bg-brand-700 disabled:opacity-50"
            >
              <Save size={14} />
              {loading ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  )
}

// ─── Permissions Modal ────────────────────────────────────────────────────────

interface PermissionsModalProps {
  user: KsecUser | null
  open: boolean
  canEdit: boolean
  onClose: () => void
  catalog: AssetCatalog
}

function PermissionsModal({ user, open, canEdit, onClose, catalog }: PermissionsModalProps) {
  const { identity, org } = useAppStore()
  const [perms, setPerms] = useState<KsecPermission[]>([])
  const [rolesCatalog, setRolesCatalog] = useState<KsecRoleCatalog[]>([])
  const [loadingPerms, setLoadingPerms] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [addingId, setAddingId] = useState<string | null>(null)
  const [showAddPanel, setShowAddPanel] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [expandedRoleId, setExpandedRoleId] = useState<string | null>(null)

  // Privilegios directos
  const [privileges, setPrivileges] = useState<KsecPrivilege[]>([])
  const [showAddPrivilege, setShowAddPrivilege] = useState(false)
  const [newPrivItem, setNewPrivItem] = useState('')
  const [newPrivAction, setNewPrivAction] = useState<ActionType>(ActionEnum.R)
  const [savingPriv, setSavingPriv] = useState(false)
  const [removingPrivId, setRemovingPrivId] = useState<string | null>(null)

  // Caché de entitlements para los roles del usuario
  const roleEntitlements = useRoleEntitlements()

  const tenantId = org.current?.businessTenantId

  const reload = useCallback(async () => {
    if (!user) return
    const userPublicId = user.subjectPartyRolePublicId ?? user.subjectPartyPublicId
    if (!userPublicId) { setPerms([]); return }
    const res = await listPermissions(userPublicId, true, tenantId ?? undefined)
    setPerms(res.data ?? [])
  }, [user, tenantId])

  useEffect(() => {
    if (!open || !user) return
    setLoadingPerms(true)
    setError(null)
    setShowAddPanel(false)
    setActionError(null)

    const loadAll = async () => {
      const userPublicId = user.subjectPartyRolePublicId ?? user.subjectPartyPublicId
      if (!userPublicId) { setPerms([]); setLoadingPerms(false); return }

      const [permsRes, catalogRes] = await Promise.allSettled([
        listPermissions(userPublicId, true, tenantId ?? undefined),
        getRolesCatalog(identity.ksecId, tenantId ?? undefined),
      ])

      if (permsRes.status === 'fulfilled') {
        const loadedPerms = permsRes.value.data ?? []
        setPerms(loadedPerms)
        // Cargar privilegios del primer permiso encontrado
        if (loadedPerms[0]) {
          listPrivileges(loadedPerms[0].id)
            .then((r) => setPrivileges(Array.isArray(r.data) ? r.data : []))
            .catch(() => setPrivileges([]))
        }
      } else {
        setError('No se pudo cargar los permisos.')
      }

      if (catalogRes.status === 'fulfilled') {
        const raw = catalogRes.value.data as { items?: KsecRoleCatalog[] } | KsecRoleCatalog[]
        setRolesCatalog(Array.isArray(raw) ? raw : (raw?.items ?? []))
      }
      setLoadingPerms(false)
    }

    loadAll()
  }, [open, user, tenantId, identity.ksecId])

  useEffect(() => {
    if (!open) {
      setPerms([]); setRolesCatalog([]); setError(null)
      setActionError(null); setExpandedRoleId(null)
      setPrivileges([]); setShowAddPrivilege(false)
      setNewPrivItem(''); setNewPrivAction('R')
    }
  }, [open])

  // El permission base del usuario en esta org (usar el primero)
  const basePerm = perms[0]
  const assignedRoles = perms.flatMap((p) => p.assignedRoles)
  const assignedRoleIds = new Set(assignedRoles.map((r) => r.roleId))
  const availableToAdd = rolesCatalog.filter((r) => !assignedRoleIds.has(r.id))

  async function handleAddPrivilege() {
    if (!basePerm || !newPrivItem) return
    setSavingPriv(true)
    setActionError(null)
    try {
      const entry = [...catalog.byTag.values()].find((e) => e.itemPublicId === newPrivItem)
      await createPrivilege(basePerm.id, {
        digitalAssetItemPublicId: newPrivItem,
        action: newPrivAction,
        function: `${entry?.itemName ?? newPrivItem} — ${newPrivAction}`,
      })
      const res = await listPrivileges(basePerm.id)
      setPrivileges(Array.isArray(res.data) ? res.data : [])
      setShowAddPrivilege(false)
      setNewPrivItem('')
      setNewPrivAction('R')
    } catch {
      setActionError('No se pudo guardar el privilegio.')
    } finally {
      setSavingPriv(false)
    }
  }

  async function handleDeletePrivilege(privId: string) {
    if (!basePerm) return
    setRemovingPrivId(privId)
    setActionError(null)
    try {
      await deletePrivilege(basePerm.id, privId)
      setPrivileges((prev) => prev.filter((p) => p.id !== privId))
    } catch {
      setActionError('No se pudo eliminar el privilegio.')
    } finally {
      setRemovingPrivId(null)
    }
  }

  if (!open || !user) return null

  async function handleRemoveRole(permId: string, aurId: string) {
    setRemovingId(aurId)
    setActionError(null)
    try {
      await revokeAssetUserRole(permId, aurId)
      await reload()
    } catch {
      setActionError('No se pudo quitar el rol. Intenta de nuevo.')
    } finally {
      setRemovingId(null)
    }
  }

  async function handleAddRole(roleId: string) {
    if (!basePerm) return
    setAddingId(roleId)
    setActionError(null)
    try {
      await addRoleToPermission(basePerm.id, roleId)
      await reload()
      if (availableToAdd.length <= 1) setShowAddPanel(false)
    } catch {
      setActionError('No se pudo agregar el rol. Intenta de nuevo.')
    } finally {
      setAddingId(null)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-primary shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border-secondary px-6 py-4">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-brand-600" />
            <div>
              <h2 className="font-display font-bold text-text-primary">
                Gestión de acceso
              </h2>
              <p className="text-xs text-text-tertiary">{user.nickname ?? user.id}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="max-h-[65vh] overflow-y-auto px-6 py-5 space-y-5">
          {/* Error de acción */}
          {actionError && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              {actionError}
            </div>
          )}

          {/* Roles asignados */}
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
              Roles asignados
            </p>

            {loadingPerms && (
              <p className="text-sm text-text-tertiary">Cargando…</p>
            )}
            {error && !loadingPerms && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                {error}
              </div>
            )}
            {!loadingPerms && !error && assignedRoles.length === 0 && (
              <p className="rounded-lg border border-border-secondary bg-bg-secondary px-4 py-3 text-sm text-text-tertiary">
                Sin roles asignados en esta organización.
              </p>
            )}
            {!loadingPerms && assignedRoles.length > 0 && (
              <div className="flex flex-col gap-2">
                {perms.map((perm) =>
                  perm.assignedRoles.map((r) => {
                    const isExpanded = expandedRoleId === r.roleId
                    return (
                      <div
                        key={r.id}
                        className="rounded-lg border border-border-secondary bg-bg-secondary"
                      >
                        {/* Fila del rol */}
                        <div className="flex items-center gap-3 px-4 py-3">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-xs font-bold text-brand-600">
                            {(r.roleName || r.involvementRole || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-text-primary">{r.roleName}</p>
                            <p className="truncate text-xs text-text-tertiary">{r.involvementRole ?? '—'}</p>
                          </div>
                          {/* Toggle ver accesos */}
                          <button
                            type="button"
                            onClick={() => {
                              if (isExpanded) { setExpandedRoleId(null); return }
                              setExpandedRoleId(r.roleId)
                              roleEntitlements.loadFor(r.roleId)
                            }}
                            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-text-tertiary hover:text-brand-600"
                            title={isExpanded ? 'Ocultar accesos' : 'Ver accesos'}
                          >
                            {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                          </button>
                          {canEdit && (
                            <button
                              onClick={() => handleRemoveRole(perm.id, r.id)}
                              disabled={removingId === r.id}
                              title="Quitar rol"
                              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-text-tertiary transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                            >
                              {removingId === r.id ? (
                                <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                                </svg>
                              ) : (
                                <X size={14} />
                              )}
                            </button>
                          )}
                        </div>

                        {/* Accesos expandidos por producto */}
                        {isExpanded && (
                          <div className="border-t border-border-secondary px-4 pb-3 pt-3">
                            <RoleAccessCard
                              entitlements={roleEntitlements.get(r.roleId) ?? []}
                              loading={roleEntitlements.isLoading(r.roleId)}
                              catalog={catalog}
                              compact
                            />
                          </div>
                        )}
                      </div>
                    )
                  })
                )}
              </div>
            )}
          </div>

          {/* Panel agregar rol */}
          {canEdit && basePerm && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
                  Agregar rol
                </p>
                {!showAddPanel && availableToAdd.length > 0 && (
                  <button
                    onClick={() => setShowAddPanel(true)}
                    className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-brand-600 hover:bg-brand-50"
                  >
                    <Plus size={12} />
                    Agregar
                  </button>
                )}
              </div>

              {showAddPanel && (
                availableToAdd.length === 0 ? (
                  <p className="text-sm text-text-tertiary">
                    Todos los roles disponibles ya están asignados.
                  </p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {availableToAdd.map((r) => (
                      <div
                        key={r.id}
                        className="flex items-center gap-3 rounded-lg border border-dashed border-border-secondary bg-bg-primary px-4 py-3"
                      >
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-bg-secondary text-xs font-bold text-text-tertiary">
                          {(r.name || r.involvementRole).charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-text-primary">{r.name}</p>
                          <p className="truncate text-xs text-text-tertiary">{r.involvementRole}</p>
                        </div>
                        <button
                          onClick={() => handleAddRole(r.id)}
                          disabled={!!addingId}
                          title="Asignar rol"
                          className="flex items-center gap-1 rounded-md border border-brand-300 bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 transition-colors hover:bg-brand-100 disabled:opacity-40"
                        >
                          {addingId === r.id ? (
                            <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                            </svg>
                          ) : (
                            <Plus size={11} />
                          )}
                          Asignar
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => setShowAddPanel(false)}
                      className="text-xs text-text-tertiary hover:text-text-secondary"
                    >
                      Cancelar
                    </button>
                  </div>
                )
              )}

              {!showAddPanel && availableToAdd.length === 0 && assignedRoles.length > 0 && (
                <p className="text-xs text-text-tertiary">
                  Todos los roles disponibles ya están asignados.
                </p>
              )}
            </div>
          )}

          {/* ── Privilegios directos ── */}
          {canEdit && basePerm && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
                    Privilegios directos
                  </p>
                  <p className="text-xs text-text-quaternary mt-0.5">
                    Accesos por ítem, sin pasar por roles. Opciones: ALL, RW, R.
                  </p>
                </div>
                {!showAddPrivilege && (
                  <button
                    onClick={() => setShowAddPrivilege(true)}
                    className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-brand-600 hover:bg-brand-50"
                  >
                    <Plus size={12} />
                    Agregar
                  </button>
                )}
              </div>

              {/* Lista de privilegios existentes */}
              {privileges.length === 0 && !showAddPrivilege && (
                <p className="text-xs text-text-tertiary">Sin privilegios directos asignados.</p>
              )}
              {privileges.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  {privileges.map((priv) => {
                    const entry = [...catalog.byTag.values()].find(
                      (e) => e.itemPublicId === priv.digitalAssetItemId,
                    )
                    const itemLabel = entry?.itemName ?? priv.digitalAssetItemId.slice(0, 12) + '…'
                    const assetLabel = entry?.assetName
                    return (
                      <div
                        key={priv.id}
                        className="flex items-center justify-between gap-2 rounded-lg border border-border-secondary bg-bg-secondary px-3 py-2"
                      >
                        <div className="min-w-0">
                          {assetLabel && (
                            <p className="text-xs text-text-tertiary">{assetLabel}</p>
                          )}
                          <p className="truncate text-xs font-medium text-text-primary">{itemLabel}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <ActionBadge action={priv.action} />
                          <button
                            onClick={() => handleDeletePrivilege(priv.id)}
                            disabled={removingPrivId === priv.id}
                            className="flex h-6 w-6 items-center justify-center rounded text-text-tertiary hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                          >
                            {removingPrivId === priv.id ? (
                              <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                              </svg>
                            ) : <X size={12} />}
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Formulario agregar privilegio */}
              {showAddPrivilege && (
                <div className="rounded-lg border border-dashed border-brand-300 bg-brand-50/40 p-3 space-y-3">
                  <p className="text-xs font-medium text-text-secondary">Nuevo privilegio directo</p>

                  {/* Selector de DigitalAssetItem */}
                  <div className="space-y-1">
                    <label className="text-xs text-text-tertiary">Ítem (función / pantalla)</label>
                    <select
                      value={newPrivItem}
                      onChange={(e) => setNewPrivItem(e.target.value)}
                      className="w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary focus:border-border-brand focus:outline-none"
                    >
                      <option value="">— Selecciona un ítem —</option>
                      {catalog.assets.map((asset) => {
                        const assetItems = [...catalog.byTag.values()].filter(
                          (e) => e.assetId === asset.id,
                        )
                        if (assetItems.length === 0) return null
                        return (
                          <optgroup key={asset.id} label={asset.name}>
                            {assetItems.map((item) => (
                              <option key={item.itemPublicId} value={item.itemPublicId}>
                                {item.itemName}
                              </option>
                            ))}
                          </optgroup>
                        )
                      })}
                    </select>
                  </div>

                  {/* Selector de acción */}
                  <div className="space-y-1">
                    <label className="text-xs text-text-tertiary">Nivel de acceso</label>
                    <div className="flex flex-wrap gap-2">
                      {([ActionEnum.ALL, ActionEnum.RW, ActionEnum.R] as ActionType[]).map((a) => (
                        <button
                          key={a}
                          type="button"
                          onClick={() => setNewPrivAction(a)}
                          className={cn(
                            'rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
                            newPrivAction === a
                              ? 'border-brand-300 bg-brand-50 text-brand-700'
                              : 'border-border-secondary bg-bg-secondary text-text-secondary hover:border-border-primary',
                          )}
                        >
                          {a === ActionEnum.ALL && 'Completo'}
                          {a === ActionEnum.RW  && 'Leer y escribir'}
                          {a === ActionEnum.R   && 'Solo lectura'}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={handleAddPrivilege}
                      disabled={!newPrivItem || savingPriv}
                    >
                      {savingPriv ? 'Guardando…' : 'Guardar'}
                    </Button>
                    <Button variant="secondary" onClick={() => { setShowAddPrivilege(false); setNewPrivItem(''); setNewPrivAction('R') }}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-border-secondary px-6 py-4 flex justify-end">

          <Button variant="secondary" onClick={onClose}>Cerrar</Button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function UsuariosPage() {
  const { canSee, canWrite } = useAccess()
  const { identity, org, roles } = useAppStore()

  // Catálogo global de assets: se carga una vez para todos los modales
  const catalog = useAssetCatalog()

  const [search, setSearch] = useState('')
  const [users, setUsers] = useState<KsecDigitalIdentity[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingUser, setEditingUser] = useState<KsecDigitalIdentity | null>(null)
  const [permissionsUser, setPermissionsUser] = useState<KsecUser | null>(null)
  const [deactivating, setDeactivating] = useState<string | null>(null)

  // Un usuario puede crear/editar si:
  //  a) tiene la acción RW/ALL en el access map (entitlement KSEC configurado), O
  //  b) tiene rol ADMIN en su organización (involvementRole), O
  //  c) es KashioSuper
  const hasAdminRole =
    identity.isKashioSuper ||
    Object.keys(roles.map).some((r) =>
      ['ADMIN', 'SYSTEM_ADMIN', 'KASHIO_ADMIN', 'KASHIO_SUPER_ADMIN', 'CUSTOMER_ADMINISTRATOR'].includes(r),
    )

  const canCreate           = canWrite('pe.usuarios') || hasAdminRole
  const canEdit             = canWrite('pe.usuarios') || hasAdminRole
  const canDeactivate       = canWrite('pe.usuarios') || hasAdminRole
  const canManagePermissions = canSee('pe.usuarios')  || hasAdminRole

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const tenantId = org.current?.businessTenantId
      if (!tenantId) {
        setUsers([])
        return
      }

      // Paso 1: obtener los userPublicIds que tienen permiso en esta org.
      // En v3.0 el scope de organización vive en Permission.businessTenantPublicId,
      // no en DigitalIdentity (campo eliminado en v3.0).
      const permsRes = await listOrgPermissions(tenantId)
      const orgUserIds = new Set(
        (permsRes.data ?? [])
          .map((p) => p.user?.publicId)
          .filter((id): id is string => !!id),
      )

      if (orgUserIds.size === 0) {
        setUsers([])
        return
      }

      // Paso 2: traer todas las identidades y filtrar por pertenencia a la org.
      // Para identidades individuales → individual_identified (subjectPartyPublicId)
      // Para identidades de rol (org admin) → party_role_identified (partyRoleIdentified[])
      const allRes = await listIdentities({ page: 1, size: 200 })
      const orgUsers = (allRes.data ?? []).filter(
        (di) =>
          (di.individualIdentified && orgUserIds.has(di.individualIdentified)) ||
          (di.partyRoleIdentified ?? []).some((id) => orgUserIds.has(id)),
      )
      setUsers(orgUsers)
    } catch {
      setError('No se pudo cargar la lista de usuarios.')
    } finally {
      setLoading(false)
    }
  }, [org.current?.businessTenantId])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const filtered = users.filter((u) => {
    const q = search.toLowerCase()
    return (u.nickname ?? '').toLowerCase().includes(q)
  })

  async function handleDeactivate(user: KsecUser) {
    if (!confirm(`¿Desactivar a ${user.nickname ?? user.id}?`)) return

    // Guard server-side v3.0: usa el tag real de BD
    const allowed = await guardAction(identity.ksecId, 'pe.usuarios', 'RW')
    if (!allowed) {
      alert('No tienes permisos para desactivar usuarios.')
      return
    }

    setDeactivating(user.id)
    try {
      await updateIdentity(user.id, { status: 'INACTIVE' })
      await fetchUsers()
    } catch {
      alert('Error al desactivar el usuario.')
    } finally {
      setDeactivating(null)
    }
  }

  // Map KsecDigitalIdentity (v3.0) → KsecUser
  // email no existe en v3.0 DigitalIdentity; mostramos el ID truncado como referencia
  const tableData: KsecUser[] = filtered.map((u) => ({
    id: u.id,
    nickname: u.nickname ?? u.id,
    email: `ID: ${u.id.slice(0, 8)}…`,
    status: (u.status as KsecUser['status']) ?? 'PENDING',
    subjectPartyPublicId: u.subjectPartyPublicId,
    subjectPartyRolePublicId: u.subjectPartyRolePublicId,
    creationDate: u.creationDate,
    lastUpdate: u.lastUpdate,
  }))

  return (
    <div className="space-y-6">
      <PageHeader
        title="Usuarios"
        description="Gestiona los usuarios con acceso al portal."
        action={
          canCreate ? (
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus size={16} />
              Nuevo usuario
            </Button>
          ) : undefined
        }
      />

      <SearchInput
        placeholder="Buscar por nombre o email..."
        value={search}
        onChange={setSearch}
      />

      <UsuariosTable
        data={tableData}
        loading={loading || !!deactivating}
        error={error}
        total={filtered.length}
        canEdit={canEdit}
        canDeactivate={canDeactivate}
        canManagePermissions={canManagePermissions}
        onEdit={(u) => setEditingUser(users.find((x) => x.id === u.id) ?? null)}
        onDeactivate={handleDeactivate}
        onManagePermissions={(u) => setPermissionsUser(u)}
      />

      <CreateUserModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={fetchUsers}
        catalog={catalog}
      />

      <EditUserModal
        user={editingUser}
        open={!!editingUser}
        onClose={() => setEditingUser(null)}
        onUpdated={fetchUsers}
      />

      <PermissionsModal
        user={permissionsUser}
        open={!!permissionsUser}
        canEdit={canEdit}
        onClose={() => setPermissionsUser(null)}
        catalog={catalog}
      />
    </div>
  )
}
